import { Spinner } from "@via/components/loading/loading.tsx";
import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component";
import { ERROR } from "@via/utils/constants.tsx";
import { type JSX, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  useUserPreferences,
  useUpdateUserPreferences,
} from "@via/pages/account/profile/user-profile/user-profile.api.tsx";

export default function Preferences(): JSX.Element {
  const {
    data: userPreferences,
    isLoading: isUserPreferencesLoading,
    isError: isUserPreferencesError,
    isSuccess: isUserPreferencesSuccess,
  } = useUserPreferences();

  const {
    mutate: updateUserPreferences,
    isPending: isUpdateUserPreferencesLoading,
  } = useUpdateUserPreferences();

  const { control, handleSubmit, setValue } = useForm({
    mode: "onChange",
  });

  useEffect(() => {
    if (isUserPreferencesSuccess) {
      setValue("interdelDelay", userPreferences.interdelDelay);
      setValue(
        "workbenchAutoStopTimeoutMinutes",
        userPreferences.workbenchAutoStopTimeoutMinutes
      );
      setValue(
        "emailNotificationsEnabled",
        userPreferences.emailNotificationsEnabled === "true"
      );
    }
  }, [isUserPreferencesSuccess]);

  const handleUpdatePreferences = (payload): void => {
    updateUserPreferences({
      ...payload,
      emailNotificationsEnabled: payload.emailNotificationsEnabled
        ? "true"
        : "false",
    });
  };

  const onSubmit = handleSubmit(handleUpdatePreferences);

  return (
    <div className="p-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:w-full sm:rounded-lg">
      <div className="via-page-heading-wrapper">
        <h2 className="via-h2">Preferences</h2>
      </div>

      {isUserPreferencesLoading ? (
        <div className="py-6">
          <SkeletonLoader count={1} />
        </div>
      ) : isUserPreferencesError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <form
          onSubmit={e => {
            e.preventDefault();
            onSubmit(e);
          }}>
          <div className="divide-y divide-gray-200">
            <div className="py-5" data-testid="userProfilePreferences">
              <FormInputComponent
                control={control}
                name="interdelDelay"
                label="Intermediate Files Cleanup Delay (Hours)"
                type="number"
                min={0}
                className="w-80"
              />
            </div>
            {userPreferences?.workbenchAutoStopTimeoutMinutes !== undefined && (
              <div className="py-5">
                <FormInputComponent
                  control={control}
                  name="workbenchAutoStopTimeoutMinutes"
                  label="Workbench Auto-Stop Timeout (Minutes)"
                  type="number"
                  min={0}
                  className="w-80"
                />
              </div>
            )}
            <div className="py-5">
              <label className="inline-flex items-end gap-3">
                <FormInputComponent
                  control={control}
                  name="emailNotificationsEnabled"
                  type="checkbox"
                  className="h-6 w-6"
                />
                <span className="text-sm font-medium leading-6 text-gray-900">
                  Email me when runs finish or fail
                </span>
              </label>
            </div>
          </div>
          <button
            id="update-user-profile-preferences"
            type="submit"
            disabled={
              isUserPreferencesLoading || isUpdateUserPreferencesLoading
            }
            className="mt-6 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
            <Spinner
              loading={
                isUserPreferencesLoading || isUpdateUserPreferencesLoading
              }>
              Save
            </Spinner>
          </button>
        </form>
      )}
    </div>
  );
}
